import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useCreateDomainMutation,
  useUpdateDomainByIdMutation
} from '../../redux/apis/domainsManagement/domains/dMDomainsApiSlice'
import { toast } from 'react-toastify'
import {
  CardBody,
  CardFooter,
  CardHeader
} from '../../components/other/card/card'
import { useFormik } from 'formik'
import { getUserId } from '../../helpers/auth'
import * as Yup from 'yup'
import { Input, Select } from 'antd'
import LanguageDropdown from '../../components/LanguageDropdown'
import DomainCategoryDropdown from '../../components/DomainCategoryDropdown'
import Loader from '../../pages/pages/loader'

const DomainInfoEdit = ({ id, data }) => {
  let navigate = useNavigate()

  const [create, { isLoading: createLoading, error: createError }] =
    useCreateDomainMutation()

  const [update, { isLoading: updateLoading, error: updateError }] =
    useUpdateDomainByIdMutation()

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      domain_name: data?.data?.domain_name,
      language: data?.data?.language?.id,
      domain_category: data?.data?.domain_category?.id,
      is_active: data?.data?.is_active ?? true,
      to_crawl: data?.data?.to_crawl ?? false,
      reporting_abuse_to: data?.data?.reporting_abuse_to ?? 'isp_email',
      domain_abuse_email: data?.data?.domain_abuse_email,
      domain_abuse_form: data?.data.domain_abuse_form,
      domain_proxy_abuse_email: data?.data.domain_proxy_abuse_email,
      domain_proxy_abuse_form: data?.data.domain_proxy_abuse_form,
      crawling_priority: data?.data.crawling_priority ?? 2,
      last_updated_by_id: getUserId()
    },

    validationSchema: Yup.object({
      domain_name: Yup.string().required('Please Enter Domain Name'),
      language: Yup.number().required('Please Enter Domain Language'),
      domain_category: Yup.number().required('Please Enter Domain Category'),
      is_active: Yup.boolean().required('Please Enter Domain Status'),
      to_crawl: Yup.boolean().required('Please Enter Domain To Crawl'),
      crawling_priority: Yup.number().required(
        'Please Enter Domain Crawl periority'
      ),
      reporting_abuse_to: Yup.string().required(
        'Please Enter Reporting Abuse To'
      ),
      domain_abuse_email: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'website_email'
            ? schema.trim().required('Please enter domain abuse email')
            : schema.nullable()
      ),
      domain_abuse_form: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'website_form'
            ? schema.trim().required('Please enter domain abuse form')
            : schema.nullable()
      ),
      domain_proxy_abuse_email: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'proxy_email'
            ? schema.trim().required('Please enter isp abuse email')
            : schema.nullable()
      ),
      domain_proxy_abuse_form: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'proxy_form'
            ? schema.trim().required('Please enter isp abuse form')
            : schema.nullable()
      )
    }),

    onSubmit: async values => {
      try {
        let response
        if (id == 0) {
          response = await create(values).unwrap()
        } else {
          response = await update({ id: id, body: values }).unwrap()
        }

        if (Array.isArray(response?.errors) && response.errors.length > 0) {
          const savedValues = { ...values }
          response.errors.forEach(({ domain_name, error }) => {
            toast.error(`${domain_name}: ${error}`)
          })
          setTimeout(() => validation.setValues(savedValues), 0)
          return
        }

        toast.success(id == 0 ? 'Domain Created successfully!' : 'Domain Saved successfully!')
        navigate(`/domains/domains-info`)
      } catch (error) {
        console.error(error)
        if (!createError && !updateError) {
          toast.error('An error occurred. Please try again.')
        }
      }
    }
  })

  // Helper to extract error messages from nested API error structure:
  // errors: [{ domain_name, errors: { errors: [{ field, message }] } }]
  const extractErrorMessages = errorData => {
    const messages = []

    const errorsArray = errorData?.errors
    if (Array.isArray(errorsArray)) {
      errorsArray.forEach(item => {
        const innerErrors = item?.errors?.errors
        if (Array.isArray(innerErrors)) {
          innerErrors.forEach(e => e.message && messages.push(e.message))
        }
      })
    }

    if (messages.length === 0) {
      const fallback =
        errorData?.detail || errorData?.message || errorData?.error
      if (fallback) messages.push(String(fallback))
    }

    return messages
  }

  useEffect(() => {
    const createErrors = extractErrorMessages(createError?.data)
    const updateErrors = extractErrorMessages(updateError?.data)
    const allMessages = [...createErrors, ...updateErrors]

    if (allMessages.length > 0) {
      allMessages.forEach(message => toast.error(message))
    } else if (createError || updateError) {
      toast.error('An error occurred. Please try again.')
    }
  }, [createError, updateError])


  return (
    <>
      <Loader isLoading={createLoading || updateLoading} />
      <CardHeader>
        {id == 0 ? 'CREATE DOMAIN' : 'EDIT DOMAIN |'} {data?.data?.domain_name}
      </CardHeader>
      <CardBody>
        <div className='flex w-full flex-col '>
          <div className=' mb-3 flex-1 customInput'>
            <label className="form-label">Domain name</label>
            <Input
              type='text'
              name='domain_name'
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.domain_name}
            />
            {validation.touched.domain_name && validation.errors.domain_name ? (
              <p className='text-danger'>{validation.errors.domain_name}</p>
            ) : null}
          </div>

          <LanguageDropdown
            onBlur={validation.handleBlur}
            onChange={e => {
              validation.setFieldValue('language', e)
            }}
            value={validation.values?.language}
            className='customSelect'
          />
          {validation.touched.language && validation.errors.language ? (
            <p className='text-danger'>{validation.errors.language}</p>
          ) : null}

          <DomainCategoryDropdown
            onBlur={validation.handleBlur}
            onChange={e => {
              validation.setFieldValue('domain_category', e)
            }}
            value={validation.values?.domain_category}
            className='customSelect'
          />
          {validation.touched.domain_category &&
          validation.errors.domain_category ? (
            <p className='text-danger'>{validation.errors.domain_category}</p>
          ) : null}

          <div className=' mb-3 flex-1'>
            <label>Domain Status</label>
            <br />
            <Select
              className={'customSelect'}
              name='is_active'
              style={{ width: '100%' }}
              defaultValue={validation?.values?.is_active}
              value={validation?.values?.is_active}
              onChange={e => validation.setFieldValue('is_active', e)}
              onBlur={validation.handleBlur}
              options={[
                { label: 'Active', value: true },
                { label: 'Inactive', value: false }
              ]}
            />
            {validation.touched.is_active && validation.errors.is_active ? (
              <p className='text-danger'>{validation.errors.is_active}</p>
            ) : null}
          </div>

          <div className=' mb-3 flex-1'>
            <label>To Crawl</label>
            <br />
            <Select
              className={'customSelect'}
              name='to_crawl'
              style={{ width: '100%' }}
              defaultValue={validation?.values?.to_crawl}
              value={validation?.values?.to_crawl}
              onChange={e => validation.setFieldValue('to_crawl', e)}
              onBlur={validation.handleBlur}
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false }
              ]}
            />
            {validation.touched.to_crawl && validation.errors.to_crawl ? (
              <p className='text-danger'>{validation.errors.to_crawl}</p>
            ) : null}
          </div>

          <div className='mb-3 flex-1'>
            <label>Domain Reporting Abuse To:</label>
            <div className='mt-2'>
              <div className='mb-2'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='reporting_abuse_to'
                    value='isp_email'
                    checked={validation.values.reporting_abuse_to === 'isp_email'}
                    onChange={() => validation.setFieldValue('reporting_abuse_to', 'isp_email')}
                  />
                  ISP Email
                </label>
              </div>

              <div className='mb-2'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='reporting_abuse_to'
                    value='isp_form'
                    checked={validation.values.reporting_abuse_to === 'isp_form'}
                    onChange={() => validation.setFieldValue('reporting_abuse_to', 'isp_form')}
                  />
                  ISP Form
                </label>
              </div>

              <div className='mb-2'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='reporting_abuse_to'
                    value='website_email'
                    checked={validation.values.reporting_abuse_to === 'website_email'}
                    onChange={() => validation.setFieldValue('reporting_abuse_to', 'website_email')}
                  />
                  Report infringement to same website abuse email
                </label>
                <div className='mt-1 customInput'>
                  <Input
                    type='text'
                    name='domain_abuse_email'
                    placeholder='Enter Abuse Email'
                    disabled={validation.values.reporting_abuse_to !== 'website_email'}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.domain_abuse_email || ''}
                  />
                  {validation.touched.domain_abuse_email &&
                  validation.errors.domain_abuse_email ? (
                    <p className='text-danger'>
                      {validation.errors.domain_abuse_email}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className='mb-2'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='reporting_abuse_to'
                    value='website_form'
                    checked={validation.values.reporting_abuse_to === 'website_form'}
                    onChange={() => validation.setFieldValue('reporting_abuse_to', 'website_form')}
                  />
                  Report infringement to same website abuse form
                </label>
                <div className='mt-1 customInput'>
                  <Input
                    type='text'
                    name='domain_abuse_form'
                    placeholder='Enter Domain Abuse Form'
                    disabled={validation.values.reporting_abuse_to !== 'website_form'}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.domain_abuse_form || ''}
                  />
                  {validation.touched.domain_abuse_form &&
                  validation.errors.domain_abuse_form ? (
                    <p className='text-danger'>
                      {validation.errors.domain_abuse_form}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className='mb-2'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='reporting_abuse_to'
                    value='proxy_email'
                    checked={validation.values.reporting_abuse_to === 'proxy_email'}
                    onChange={() => validation.setFieldValue('reporting_abuse_to', 'proxy_email')}
                  />
                  Report infringement to real ISP abuse email that is provided by proxy service
                </label>
                <div className='mt-1 customInput'>
                  <Input
                    type='text'
                    name='domain_proxy_abuse_email'
                    placeholder='Enter Domain Proxy Abuse Email'
                    disabled={validation.values.reporting_abuse_to !== 'proxy_email'}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.domain_proxy_abuse_email || ''}
                  />
                  {validation.touched.domain_proxy_abuse_email &&
                  validation.errors.domain_proxy_abuse_email ? (
                    <p className='text-danger'>
                      {validation.errors.domain_proxy_abuse_email}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className='mb-2'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='reporting_abuse_to'
                    value='proxy_form'
                    checked={validation.values.reporting_abuse_to === 'proxy_form'}
                    onChange={() => validation.setFieldValue('reporting_abuse_to', 'proxy_form')}
                  />
                  Report infringement to real ISP abuse form that is provided by proxy service
                </label>
                <div className='mt-1 customInput'>
                  <Input
                    type='text'
                    name='domain_proxy_abuse_form'
                    placeholder='Enter Domain Proxy Abuse Form'
                    disabled={validation.values.reporting_abuse_to !== 'proxy_form'}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.domain_proxy_abuse_form || ''}
                  />
                  {validation.touched.domain_proxy_abuse_form &&
                  validation.errors.domain_proxy_abuse_form ? (
                    <p className='text-danger'>
                      {validation.errors.domain_proxy_abuse_form}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            {validation.touched.reporting_abuse_to &&
            validation.errors.reporting_abuse_to ? (
              <p className='text-danger'>
                {validation.errors.reporting_abuse_to}
              </p>
            ) : null}
          </div>

          <div className=' mb-3 flex-1'>
            <label>Crawling Priority</label>
            <br />
            <Select
              className={'customSelect'}
              name='crawling_priority'
              style={{ width: '100%' }}
              defaultValue={validation?.values?.crawling_priority}
              value={validation?.values?.crawling_priority}
              onChange={e => validation.setFieldValue('crawling_priority', e)}
              onBlur={validation.handleBlur}
              options={[
                { label: 'Low', value: 1 },
                { label: 'Medium', value: 2 },
                { label: 'High', value: 3 }
              ]}
            />
            {validation.touched.crawling_priority &&
            validation.errors.crawling_priority ? (
              <p className='text-danger'>
                {validation.errors.crawling_priority}
              </p>
            ) : null}
          </div>
        </div>
      </CardBody>
      <CardFooter>
        <div className='flex justify-end gap-8'>
          <button
            onClick={() => {
              validation.handleSubmit()
            }}
            className='btn btn-theme'
          >
            Submit
          </button>
        </div>
      </CardFooter>
    </>
  )
}

export default DomainInfoEdit
